/**
 * Auto-tiling par voisinage (4 côtés du losange) : une cellule dont un voisin par un côté porte un ton plus
 * prioritaire reçoit, sur ce côté, une pièce de transition (bord adouci de la tuile du voisin qui déborde).
 * Les bits suivent l'ordre de `sideNeighbours` : nord-ouest, nord-est, sud-ouest, sud-est.
 * Pièces disponibles dans les atlas (tools/build-tiles.py) : 4 côtés seuls et 4 coins (deux côtés adjacents).
 */
import { CELLS, sideNeighbours } from './grid';

export const EDGE_NW = 1, EDGE_NE = 2, EDGE_SW = 4, EDGE_SE = 8;
export const PIECES: readonly number[] = [EDGE_NW | EDGE_NE, EDGE_NE | EDGE_SE, EDGE_SW | EDGE_SE, EDGE_NW | EDGE_SW, EDGE_NW, EDGE_NE, EDGE_SW, EDGE_SE];
/** Variantes de bruit par pièce (choisies par parité de cellule pour casser la répétition le long d'un bord). */
export const VARIANTS = 2;

export interface Transition { cell: number; from: string; to: string; mask: number; }

/** Clé d'atlas d'une pièce : tuile `to` débordant sur la tuile `from`, masque de côtés, variante. */
export const transitionKey = (from: string, to: string, mask: number, variant: number): string => `tx:${to}>${from}:${mask}:${variant}`;

/** Décompose un masque en pièces disponibles : coins d'abord, puis côtés seuls pour les bits restants. */
export function splitMask(mask: number): number[] {
  const out: number[] = [];
  let left = mask & 15;
  for (const p of PIECES) if ((left & p) === p) { out.push(p); left &= ~p; }
  return out;
}

/**
 * Transitions d'une grille de tons. `keys[i]` : clé de la tuile de sol de la cellule i. `priority` : tons du plus
 * débordant au moins débordant ; un ton absent n'est ni récepteur ni débordant. Une cellule reçoit une transition par
 * clé de tuile débordante (les côtés se cumulent dans le masque).
 */
export function transitions(tone: Uint8Array, keys: readonly string[], priority: readonly number[]): Transition[] {
  const rank = new Map<number, number>();
  priority.forEach((t, k) => rank.set(t, k));
  const out: Transition[] = [];
  for (let i = 0; i < CELLS; i++) {
    const r = rank.get(tone[i]!);
    if (r === undefined) continue;
    let groups: Map<string, number> | null = null;
    const nb = sideNeighbours(i);
    for (let s = 0; s < 4; s++) {
      const k = nb[s];
      if (k === undefined) continue;
      const rk = rank.get(tone[k]!);
      if (rk === undefined || rk >= r) continue;
      groups ??= new Map();
      groups.set(keys[k]!, (groups.get(keys[k]!) ?? 0) | (1 << s));
    }
    if (groups) for (const [to, mask] of groups) out.push({ cell: i, from: keys[i]!, to, mask });
  }
  return out;
}
